import { ApiResult } from "common/models";
import { HttpClient } from "common/services/http-client.service";
import {
  DocumentSaveRequest,
  DocumentSaveResult,
  DocumentSavePayload,
  EditableField
} from "../models/document-extraction.model";
import { documentExtractionConfig } from "../config/document-extraction.config";
import { DocumentSaveProcessor } from "../utils/document-save-processor.util";

/**
 * Service class for handling document save operations
 */
export class DocumentSaveService extends HttpClient {
  /**
   * Saves document configuration and extracted data
   */
  static async saveDocument(
    request: DocumentSaveRequest
  ): Promise<DocumentSaveResult> {
    try {
      // Validate the request
      const validation = DocumentSaveProcessor.validateSaveRequest(request);
      if (!validation.isValid) {
        return {
          success: false,
          error: validation.error
        };
      }

      // Create the payload
      const payload = DocumentSaveProcessor.createSavePayload(request);

      // Get API configuration
      const config = documentExtractionConfig.saveDocument(
        payload,
        request.flowId,
        request.docId
      );

      // Make the API call
      const response = await this.invokeApi<any>(config);

      if (response.hasErrors) {
        return {
          success: false,
          error: response.errors.join(", ") || "Unknown API error"
        };
      }

      // Check HTTP status
      if (response.statusCode && response.statusCode >= 400) {
        return {
          success: false,
          error: `HTTP error! status: ${response.statusCode}`
        };
      }

      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: `Error saving data: ${(error as Error).message}`
      };
    }
  }

  /**
   * Creates a save request object from component state
   */
  static createSaveRequest(
    currentConfig: any,
    editableFields: EditableField[],
    otherFields: any[],
    userContext: any,
    reextractedTokenCost: string | null,
    totalTokenCost: string,
    flowId: string | number,
    docId: string | number
  ): DocumentSaveRequest {
    const tokenCost = reextractedTokenCost || totalTokenCost;

    return {
      currentConfig,
      editableFields,
      otherFields,
      userContext,
      tokenCost,
      flowId,
      docId
    };
  }

  /**
   * Validates if a document can be saved
   */
  static canSaveDocument(
    currentConfig: any,
    userContext: any
  ): { canSave: boolean; reason?: string } {
    if (!currentConfig) {
      return {
        canSave: false,
        reason: "No configuration data available"
      };
    }

    return { canSave: true };
  }
}
